import "./CompanyReports.css";
import { usePermissions } from "./PermissionContext";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";

import { Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

const API_URL = "/api";

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString("es-CO");
};

const getErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return "No fue posible cargar los reportes.";
};

function CompanyReports() {
  const { can } = usePermissions();

  const canViewReports = can("reports.view");
  const today = new Date();

  const salesChartRef = useRef(null);
  const productsChartRef = useRef(null);
  const inventoryChartRef = useRef(null);

  const [activeTab, setActiveTab] = useState("sales");

  const [dateFrom, setDateFrom] = useState(
    formatDateInput(
      new Date(today.getFullYear(), today.getMonth(), 1)
    )
  );

  const [dateTo, setDateTo] = useState(formatDateInput(today));

  const [salesReport, setSalesReport] = useState(null);
  const [purchasesReport, setPurchasesReport] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);

  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);

  const loadReports = useCallback(async () => {
    if (!canViewReports) {
      return;
    }
    setLoading(true);

    try {
      const params = {
        date_from: dateFrom,
        date_to: dateTo,
      };

      const [
        salesResponse,
        purchasesResponse,
        inventoryResponse,
      ] = await Promise.all([
        axios.get(`${API_URL}/reports/me/sales`, {
          headers: getHeaders(),
          params,
        }),
        axios.get(`${API_URL}/reports/me/purchases`, {
          headers: getHeaders(),
          params,
        }),
        axios.get(`${API_URL}/reports/me/inventory`, {
          headers: getHeaders(),
        }),
      ]);

      setSalesReport(salesResponse.data);
      setPurchasesReport(purchasesResponse.data);
      setInventoryReport(inventoryResponse.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, canViewReports]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleDateFilter = (event) => {
    event.preventDefault();

    if (dateFrom > dateTo) {
      toast.error(
        "La fecha inicial no puede ser mayor que la fecha final."
      );
      return;
    }

    loadReports();
  };

  const salesChartData = useMemo(
    () => ({
      labels:
        salesReport?.daily_sales?.map((item) =>
          new Date(
            `${item.date}T00:00:00`
          ).toLocaleDateString("es-CO")
        ) || [],
      datasets: [
        {
          label: "Ventas netas",
          data:
            salesReport?.daily_sales?.map((item) =>
              Number(item.net_total || 0)
            ) || [],
          backgroundColor: "var(--company-primary)",
          borderRadius: 7,
          maxBarThickness: 65,
        },
      ],
    }),
    [salesReport]
  );

  const productsChartData = useMemo(
    () => ({
      labels:
        salesReport?.top_products?.map(
          (item) => item.product_name
        ) || [],
      datasets: [
        {
          label: "Ventas netas",
          data:
            salesReport?.top_products?.map((item) =>
              Number(item.total || 0)
            ) || [],
          backgroundColor: [
            "var(--company-primary)",
            "#087f5b",
            "#d97706",
            "#7c3aed",
            "#dc2626",
            "#0f766e",
            "#2563eb",
            "#c2410c",
            "#9333ea",
            "#4f46e5",
          ],
          borderWidth: 2,
          borderColor: "#ffffff",
        },
      ],
    }),
    [salesReport]
  );

  const inventoryChartData = useMemo(() => {
    const products = [...(inventoryReport?.products || [])]
      .sort((a, b) => Number(b.stock || 0) - Number(a.stock || 0))
      .slice(0, 10);

    return {
      labels: products.map((item) => item.name),
      datasets: [
        {
          label: "Stock actual",
          data: products.map((item) => Number(item.stock || 0)),
          backgroundColor: "var(--company-primary)",
          borderRadius: 7,
          maxBarThickness: 55,
        },
        {
          label: "Stock mínimo",
          data: products.map((item) =>
            Number(item.minimum_stock || 0)
          ),
          backgroundColor: "#dc2626",
          borderRadius: 7,
          maxBarThickness: 55,
        },
      ],
    };
  }, [inventoryReport]);

  const downloadExcel = () => {
    try {
      const workbook = XLSX.utils.book_new();

      const salesSummary = [
        {
          "Fecha inicial": salesReport?.date_from || dateFrom,
          "Fecha final": salesReport?.date_to || dateTo,
          "Ventas realizadas": salesReport?.sales_count || 0,
          "Ventas brutas": salesReport?.gross_total || 0,
          Reintegros: salesReport?.returns_total || 0,
          "Ventas netas": salesReport?.net_total || 0,
          "Ticket promedio": salesReport?.average_ticket || 0,
        },
      ];

      const salesDaily = (salesReport?.daily_sales || []).map(
        (item) => ({
          Fecha: item.date,
          Ventas: item.sales_count,
          "Ventas brutas": item.gross_total,
          Reintegros: item.returns_total,
          "Ventas netas": item.net_total,
        })
      );

      const topProducts = (salesReport?.top_products || []).map(
        (item) => ({
          SKU: item.sku,
          Producto: item.product_name,
          Unidades: item.quantity,
          "Total neto": item.total,
        })
      );

      const purchases = (purchasesReport?.items || []).map(
        (item) => ({
          Fecha: formatDateTime(item.date),
          SKU: item.sku,
          Producto: item.product_name,
          Categoría: item.category || "Sin categoría",
          Cantidad: item.quantity,
          "Costo unitario estimado": item.estimated_unit_cost,
          "Costo total estimado": item.estimated_total,
          Concepto: item.concept,
        })
      );

      const inventory = (inventoryReport?.products || []).map(
        (item) => ({
          SKU: item.sku,
          Producto: item.name,
          Categoría: item.category || "Sin categoría",
          Stock: item.stock,
          "Stock mínimo": item.minimum_stock,
          "Precio compra": item.purchase_price,
          "Precio venta": item.sale_price,
          "Valor al costo": item.cost_value,
          "Valor potencial venta": item.sale_value,
          Estado: item.is_low_stock
            ? "Stock mínimo"
            : "Disponible",
        })
      );

      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(salesSummary),
        "Resumen ventas"
      );

      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(salesDaily),
        "Ventas por día"
      );

      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(topProducts),
        "Productos vendidos"
      );

      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(purchases),
        "Compras y entradas"
      );

      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(inventory),
        "Inventario"
      );

      XLSX.writeFile(
        workbook,
        `reporte_empresa_${dateFrom}_${dateTo}.xlsx`
      );

      toast.success("Reporte Excel generado correctamente.");
    } catch (error) {
      toast.error("No fue posible generar el archivo Excel.");
    }
  };

  const downloadPdf = () => {
    try {
      setExportingPdf(true);

      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const companyInfo = JSON.parse(
        localStorage.getItem("company_info") || "{}"
      );

      const companyName =
        companyInfo.company_name || "Mi empresa";

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;

      const addHeader = (title) => {
        pdf.setFillColor(31, 93, 122);
        pdf.rect(0, 0, pageWidth, 23, "F");

        pdf.setTextColor(255, 255, 255);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(15);
        pdf.text(companyName, margin, 10);

        pdf.setFontSize(10);
        pdf.text(title, margin, 17);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);

        pdf.text(
          `Período: ${dateFrom} a ${dateTo}`,
          pageWidth - margin,
          10,
          { align: "right" }
        );

        pdf.text(
          `Generado: ${new Date().toLocaleString("es-CO")}`,
          pageWidth - margin,
          17,
          { align: "right" }
        );

        pdf.setTextColor(30, 41, 59);
      };

      const addMetric = (x, y, title, value, color) => {
        pdf.setFillColor(248, 250, 252);
        pdf.setDrawColor(226, 232, 240);
        pdf.roundedRect(x, y, 50, 25, 3, 3, "FD");

        pdf.setTextColor(100, 116, 139);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(7);
        pdf.text(title, x + 4, y + 8);

        pdf.setTextColor(color[0], color[1], color[2]);
        pdf.setFontSize(12);
        pdf.text(value, x + 4, y + 18);

        pdf.setTextColor(30, 41, 59);
      };

      const addChart = (chartRef, x, y, width, height) => {
        const chart = chartRef.current;

        if (!chart || typeof chart.toBase64Image !== "function") {
          return;
        }

        const image = chart.toBase64Image("image/png", 1);

        pdf.setDrawColor(226, 232, 240);
        pdf.roundedRect(x, y, width, height, 3, 3, "S");

        pdf.addImage(
          image,
          "PNG",
          x + 4,
          y + 4,
          width - 8,
          height - 8
        );
      };

      const addFooter = () => {
        const totalPages = pdf.getNumberOfPages();

        for (let page = 1; page <= totalPages; page += 1) {
          pdf.setPage(page);

          pdf.setDrawColor(226, 232, 240);
          pdf.line(
            margin,
            pageHeight - 12,
            pageWidth - margin,
            pageHeight - 12
          );

          pdf.setTextColor(100, 116, 139);
          pdf.setFontSize(7);

          pdf.text(
            `${companyName} · Reporte interno`,
            margin,
            pageHeight - 7
          );

          pdf.text(
            `Página ${page} de ${totalPages}`,
            pageWidth - margin,
            pageHeight - 7,
            { align: "right" }
          );
        }
      };

      if (activeTab === "sales") {
        addHeader("Reporte de ventas");

        addMetric(12, 30, "VENTAS REALIZADAS",
          String(salesReport?.sales_count || 0), [31, 93, 122]);

        addMetric(65, 30, "VENTAS BRUTAS",
          money(salesReport?.gross_total), [31, 93, 122]);

        addMetric(118, 30, "REINTEGROS",
          money(salesReport?.returns_total), [185, 28, 28]);

        addMetric(171, 30, "VENTAS NETAS",
          money(salesReport?.net_total), [21, 128, 61]);

        addMetric(224, 30, "TICKET PROMEDIO",
          money(salesReport?.average_ticket), [31, 93, 122]);

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        pdf.text("Ventas netas por día", 12, 63);
        pdf.text("Productos más vendidos", 151, 63);

        addChart(salesChartRef, 12, 67, 130, 75);
        addChart(productsChartRef, 151, 67, 130, 75);

        autoTable(pdf, {
          startY: 150,
          head: [["Producto", "SKU", "Unidades", "Total neto"]],
          body: (salesReport?.top_products || []).map((item) => [
            item.product_name,
            item.sku,
            item.quantity,
            money(item.total),
          ]),
          theme: "grid",
          headStyles: {
            fillColor: [31, 93, 122],
            textColor: [255, 255, 255],
            fontStyle: "bold",
          },
          styles: { fontSize: 8, cellPadding: 3 },
          margin: { left: margin, right: margin, bottom: 18 },
        });
      }

      if (activeTab === "purchases") {
        addHeader("Reporte de compras y entradas");

        addMetric(12, 30, "ENTRADAS",
          String(purchasesReport?.entries_count || 0), [31, 93, 122]);

        addMetric(65, 30, "UNIDADES INGRESADAS",
          String(purchasesReport?.total_quantity || 0), [21, 128, 61]);

        addMetric(118, 30, "VALOR ESTIMADO",
          money(purchasesReport?.estimated_total), [8, 127, 91]);

        autoTable(pdf, {
          startY: 65,
          head: [[
            "Fecha", "Código", "Producto", "Categoría",
            "Cantidad", "Costo estimado", "Concepto",
          ]],
          body: (purchasesReport?.items || []).map((item) => [
            formatDateTime(item.date),
            item.sku,
            item.product_name,
            item.category || "Sin categoría",
            `+${item.quantity}`,
            money(item.estimated_total),
            item.concept,
          ]),
          theme: "grid",
          headStyles: {
            fillColor: [31, 93, 122],
            textColor: [255, 255, 255],
            fontStyle: "bold",
          },
          styles: { fontSize: 7, cellPadding: 2.5 },
          margin: { left: margin, right: margin, bottom: 18 },
        });
      }

      if (activeTab === "inventory") {
        addHeader("Reporte de inventario");

        addMetric(12, 30, "PRODUCTOS",
          String(inventoryReport?.products_count || 0), [31, 93, 122]);

        addMetric(65, 30, "UNIDADES EN STOCK",
          String(inventoryReport?.total_units || 0), [21, 128, 61]);

        addMetric(118, 30, "ALERTAS",
          String(inventoryReport?.low_stock_count || 0), [185, 28, 28]);

        addMetric(171, 30, "VALOR AL COSTO",
          money(inventoryReport?.inventory_cost_value), [31, 93, 122]);

        addMetric(224, 30, "MARGEN POTENCIAL",
          money(inventoryReport?.potential_margin), [21, 128, 61]);

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        pdf.text("Stock actual vs. stock mínimo", 12, 63);

        addChart(inventoryChartRef, 12, 67, 269, 70);

        autoTable(pdf, {
          startY: 145,
          head: [[
            "Código", "Producto", "Categoría", "Stock",
            "Mínimo", "Valor costo", "Valor venta", "Estado",
          ]],
          body: (inventoryReport?.products || []).map((item) => [
            item.sku,
            item.name,
            item.category || "Sin categoría",
            item.stock,
            item.minimum_stock,
            money(item.cost_value),
            money(item.sale_value),
            item.is_low_stock ? "Stock mínimo" : "Disponible",
          ]),
          theme: "grid",
          headStyles: {
            fillColor: [31, 93, 122],
            textColor: [255, 255, 255],
            fontStyle: "bold",
          },
          styles: { fontSize: 7, cellPadding: 2.5 },
          margin: { left: margin, right: margin, bottom: 18 },
        });
      }

      addFooter();

      pdf.save(
        `reporte_profesional_${activeTab}_${dateFrom}_${dateTo}.pdf`
      );

      toast.success("Reporte PDF generado correctamente.");
    } catch (error) {
      console.error(error);
      toast.error("No fue posible generar el PDF.");
    } finally {
      setExportingPdf(false);
    }
  };

  if (loading && !salesReport) {
    return <h2>Cargando reportes...</h2>;
  }

  return (
    <div className="company-reports">
      <section className="reports-heading">
        <div>
          <p className="reports-kicker">Análisis</p>
          <h1>Informes</h1>
          <p>Consulta el desempeño de ventas, compras e inventario.</p>
        </div>

        <div className="report-export-actions">
          <button
            type="button"
            className="btn-report-excel"
            onClick={downloadExcel}
          >
            Exportar Excel
          </button>

          <button
            type="button"
            className="btn-report-pdf"
            onClick={downloadPdf}
            disabled={exportingPdf}
          >
            {exportingPdf ? "Generando PDF..." : "Exportar PDF"}
          </button>
        </div>
      </section>

      <section className="reports-card report-filters-card">
        <form onSubmit={handleDateFilter} className="report-filters">
          <label>
            Desde
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
            />
          </label>

          <label>
            Hasta
            <input
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
            />
          </label>

          <button type="submit" disabled={loading}>
            {loading ? "Cargando..." : "Aplicar filtro"}
          </button>
        </form>
      </section>

      <section className="report-tabs">
        <button
          type="button"
          className={activeTab === "sales" ? "active" : ""}
          onClick={() => setActiveTab("sales")}
        >
          Ventas
        </button>

        <button
          type="button"
          className={activeTab === "purchases" ? "active" : ""}
          onClick={() => setActiveTab("purchases")}
        >
          Compras / Entradas
        </button>

        <button
          type="button"
          className={activeTab === "inventory" ? "active" : ""}
          onClick={() => setActiveTab("inventory")}
        >
          Inventario
        </button>
      </section>

      {activeTab === "sales" && (
        <>
          <section className="report-summary-grid">
            <article className="report-summary-card">
              <span>Ventas realizadas</span>
              <strong>{salesReport?.sales_count ?? 0}</strong>
            </article>

            <article className="report-summary-card">
              <span>Ventas brutas</span>
              <strong>{money(salesReport?.gross_total)}</strong>
            </article>

            <article className="report-summary-card returns">
              <span>Reintegros y anulaciones</span>
              <strong>{money(salesReport?.returns_total)}</strong>
            </article>

            <article className="report-summary-card net">
              <span>Ventas netas</span>
              <strong>{money(salesReport?.net_total)}</strong>
            </article>

            <article className="report-summary-card">
              <span>Ticket promedio</span>
              <strong>{money(salesReport?.average_ticket)}</strong>
            </article>
          </section>

          <section className="report-charts-grid">
            <article className="reports-card report-chart-card">
              <div className="report-card-heading">
                <h2>Ventas netas por día</h2>
                <p>Resultado neto del período seleccionado.</p>
              </div>

              {salesReport?.daily_sales?.length ? (
                <div className="report-chart-canvas">
                  <Bar
                    ref={salesChartRef}
                    data={salesChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                      },
                    }}
                  />
                </div>
              ) : (
                <p className="report-empty">
                  No hay ventas en este período.
                </p>
              )}
            </article>

            <article className="reports-card report-chart-card">
              <div className="report-card-heading">
                <h2>Productos más vendidos</h2>
                <p>Participación según el total neto vendido.</p>
              </div>

              {salesReport?.top_products?.length ? (
                <div className="report-chart-canvas">
                  <Doughnut
                    ref={productsChartRef}
                    data={productsChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { position: "bottom" },
                      },
                    }}
                  />
                </div>
              ) : (
                <p className="report-empty">
                  No hay productos vendidos en este período.
                </p>
              )}
            </article>
          </section>

          <section className="reports-card report-section-card">
            <div className="report-card-heading">
              <h2>Productos más vendidos</h2>
              <p>Ordenados por valor neto vendido.</p>
            </div>

            <div className="report-table-wrapper">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Unidades</th>
                    <th>Total neto</th>
                  </tr>
                </thead>

                <tbody>
                  {!salesReport?.top_products?.length ? (
                    <tr>
                      <td colSpan="3" className="report-empty">
                        Sin productos vendidos.
                      </td>
                    </tr>
                  ) : (
                    salesReport.top_products.map((product) => (
                      <tr key={`${product.sku}-${product.product_name}`}>
                        <td>
                          <strong>{product.product_name}</strong>
                          <small>{product.sku}</small>
                        </td>
                        <td>{product.quantity}</td>
                        <td className="report-amount">
                          {money(product.total)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {activeTab === "purchases" && (
        <>
          <section className="report-summary-grid report-summary-three">
            <article className="report-summary-card">
              <span>Entradas registradas</span>
              <strong>{purchasesReport?.entries_count ?? 0}</strong>
            </article>

            <article className="report-summary-card">
              <span>Unidades ingresadas</span>
              <strong>{purchasesReport?.total_quantity ?? 0}</strong>
            </article>

            <article className="report-summary-card net">
              <span>Valor estimado</span>
              <strong>{money(purchasesReport?.estimated_total)}</strong>
            </article>
          </section>

          <section className="reports-card report-section-card">
            <div className="report-card-heading">
              <h2>Compras y entradas de inventario</h2>
              <p>
                Valor estimado con el precio de compra actual del producto.
              </p>
            </div>

            <div className="report-table-wrapper">
              <table className="report-table purchases-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Código</th>
                    <th>Producto</th>
                    <th>Categoría</th>
                    <th>Cantidad</th>
                    <th>Costo estimado</th>
                    <th>Concepto</th>
                  </tr>
                </thead>

                <tbody>
                  {!purchasesReport?.items?.length ? (
                    <tr>
                      <td colSpan="7" className="report-empty">
                        Sin entradas en el período seleccionado.
                      </td>
                    </tr>
                  ) : (
                    purchasesReport.items.map((item) => (
                      <tr key={item.id}>
                        <td>{formatDateTime(item.date)}</td>
                        <td>{item.sku}</td>
                        <td>
                          <strong>{item.product_name}</strong>
                        </td>
                        <td>{item.category || "Sin categoría"}</td>
                        <td className="positive-quantity">
                          +{item.quantity}
                        </td>
                        <td className="report-amount">
                          {money(item.estimated_total)}
                        </td>
                        <td>{item.concept}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {activeTab === "inventory" && (
        <>
          <section className="report-summary-grid">
            <article className="report-summary-card">
              <span>Productos registrados</span>
              <strong>{inventoryReport?.products_count ?? 0}</strong>
            </article>

            <article className="report-summary-card">
              <span>Unidades en stock</span>
              <strong>{inventoryReport?.total_units ?? 0}</strong>
            </article>

            <article className="report-summary-card returns">
              <span>Alertas de stock mínimo</span>
              <strong>{inventoryReport?.low_stock_count ?? 0}</strong>
            </article>

            <article className="report-summary-card">
              <span>Valor al costo</span>
              <strong>
                {money(inventoryReport?.inventory_cost_value)}
              </strong>
            </article>

            <article className="report-summary-card net">
              <span>Margen potencial</span>
              <strong>
                {money(inventoryReport?.potential_margin)}
              </strong>
            </article>
          </section>

          <section className="reports-card report-chart-card report-chart-wide">
            <div className="report-card-heading">
              <h2>Stock actual vs. stock mínimo</h2>
              <p>Los diez productos con mayor disponibilidad.</p>
            </div>

            {inventoryReport?.products?.length ? (
              <div className="report-chart-canvas report-chart-canvas-wide">
                <Bar
                  ref={inventoryChartRef}
                  data={inventoryChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: "bottom" },
                    },
                  }}
                />
              </div>
            ) : (
              <p className="report-empty">
                No existen productos en inventario.
              </p>
            )}
          </section>

          <section className="reports-card report-section-card">
            <div className="report-card-heading">
              <h2>Valoración de inventario</h2>
              <p>
                Stock actual, valor de compra y valor potencial de venta.
              </p>
            </div>

            <div className="report-table-wrapper">
              <table className="report-table inventory-report-table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Producto</th>
                    <th>Categoría</th>
                    <th>Stock</th>
                    <th>Mínimo</th>
                    <th>Valor costo</th>
                    <th>Valor venta</th>
                    <th>Estado</th>
                  </tr>
                </thead>

                <tbody>
                  {!inventoryReport?.products?.length ? (
                    <tr>
                      <td colSpan="8" className="report-empty">
                        Sin productos en inventario.
                      </td>
                    </tr>
                  ) : (
                    inventoryReport.products.map((product) => (
                      <tr
                        key={product.id}
                        className={
                          product.is_low_stock
                            ? "low-stock-row"
                            : ""
                        }
                      >
                        <td>{product.sku}</td>
                        <td>
                          <strong>{product.name}</strong>
                        </td>
                        <td>{product.category || "Sin categoría"}</td>
                        <td>{product.stock}</td>
                        <td>{product.minimum_stock}</td>
                        <td className="report-amount">
                          {money(product.cost_value)}
                        </td>
                        <td className="report-amount">
                          {money(product.sale_value)}
                        </td>
                        <td>
                          <span
                            className={
                              product.is_low_stock
                                ? "report-stock-low"
                                : "report-stock-ok"
                            }
                          >
                            {product.is_low_stock
                              ? "Stock mínimo"
                              : "Disponible"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

export default CompanyReports;